import { inject, Injectable } from '@angular/core';
import { SawDust } from '@cudirect/shared-ui-library';
import { PATH_ITERATION_SYMBOL, PATH_PAGE_ITERATION_SYMBOL, StatefulValue } from '@workflow/class/stateful-value.class';
import { statefulArrayHelper } from '@workflow/class/stateful-value.helper';
import { ErrCallback, FlatData } from '@workflow/class/storage-backend.base';
import { PrimitiveType, Backend, Storage } from '@workflow/class/workflow.type';
import { ConfigService } from '@workflow/service/config/config.service';
import { StateManagementService } from '@workflow/service/state-management/state-management.service';
import { StorageInfoService } from '@workflow/service/storage-info/storage-info.service';
import { StorageService } from '@workflow/service/storage/storage.service';

const SIGNED_FLOAT_RX = /[^-0-9.]/g;
const START_SIGN_RX = /(?!^)-/g;

@Injectable({
  providedIn: 'root'
})
export class StatefulService {
  public readonly logger = inject(SawDust).withContext({ component: 'StatefulService' });
  private readonly configService = inject(ConfigService);
  private readonly storageService = inject(StorageService);
  private readonly storageInfoService = inject(StorageInfoService);
  private readonly stateManagementService = inject(StateManagementService);
  /**
   * StatefulValue store
   */
  private instances: { [id: string]: StatefulValue } = {};

  public wrap(id: string, iterationId?: number, pageIterationId?: number): StatefulValue {
    // console.log('[WRAP] called with:', id, 'backends:', this.storageService?.backends);
    const originalId = id;

    // Step 1: Extract protocol (e.g., "api")
    const protocol = id.split(':')[0];

    // Step 2: Check if backend exists for the protocol
    const allBackends = this.storageService?.backends;
    const backend = allBackends?.[protocol];

    if (!backend) {
      const available = Object.keys(allBackends).join(', ');
      throw new Error(
        `[StatefulService.wrap] No backend found for protocol "${protocol}". Key: "${id}". Available backends: [${available}]`
      );
    }

    // ✅ Safe to proceed
    let instance: StatefulValue;

    if (iterationId != null) {
      if (originalId.indexOf(PATH_ITERATION_SYMBOL) < 0) {
        iterationId = null;
      } else {
        id = originalId.replace(PATH_ITERATION_SYMBOL, String(iterationId));
      }
    } else if (originalId.indexOf(PATH_ITERATION_SYMBOL) > -1) {
      throw new Error(`Array path requires iteration ID: ${id}`);
    }

    if (pageIterationId != null) {
      if (id.indexOf(PATH_PAGE_ITERATION_SYMBOL) < 0) {
        pageIterationId = null;
      } else {
        id = id.replace(PATH_PAGE_ITERATION_SYMBOL, String(pageIterationId));
      }
    } else if (id.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1) {
      throw new Error(`Array path requires page iteration ID: ${id}`);
    }

    if (this.instances[id]) {
      return this.instances[id];
    }

    if (arguments.length === 3) {
      instance = new StatefulValue(this, id, iterationId, pageIterationId);
    } else if (arguments.length === 2) {
      instance = new StatefulValue(this, id, iterationId);
    } else {
      instance = new StatefulValue(this, id);
    }

    this.instances[id] = instance;

    return instance;
  }

  // ******** Methods that were static in the old class have now been moves to the service class ********

  /**
   * Explicit request to extend array on the back-end.
   * This should be the only way for the UI to trigger array extension (to avoid race conditions)
   * NOTE: addition on the backend may be asyncronous. Don't expect the array to be extended right after calling this method
   * Use the callback, if needed
   * @param configPath array root path or full path (must include protocol)
   */
  public arrayAdd(configPath: string, cb?: ErrCallback): void;
  public arrayAdd(configPath: string, appendData: FlatData, cb?: ErrCallback): void;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  public arrayAdd(configPath: string, arg1: FlatData | ErrCallback, arg2?: ErrCallback): void {
    const path = this.arrayInfo(configPath);
    const info = this.storageInfoService.parse(configPath);
    const processed = info?.pageIterationId != null ? path.replace(PATH_PAGE_ITERATION_SYMBOL, String(info.pageIterationId)) : path;
    if (processed.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1) {
      throw new Error('Page iteration ID required');
    }
    // The arg2 callback was never actually implemented correctly, so we are going to
    // update the code to always call the callback after the arrayAdd completes
    if (typeof arg2 === 'function') {
      this.storageService.arrayAdd(processed, arg1 as FlatData, () => {
        arg2(); // Ensure the original callback is called after arrayAdd completes
      });
    } else if (typeof arg1 === 'function') {
      // Pass a wrapper callback to arrayAdd
      this.storageService.arrayAdd(processed, () => {
        arg1(); // Ensure the original callback is called after arrayAdd completes
      });
    } else {
      this.storageService.arrayAdd(processed, arg1, arg2);
    }
  }

  /**
   * Explicit request to extend array
   * This should be the only way for the UI to trigger array shrinking (to avoid race conditions)
   * NOTE: addition on the backend may be asyncronous. Don't expect the array to be changed right after calling this method.
   * Use the callback, if needed
   * @param configPath including iterartion ID (not $)
   */
  public arrayRemove(configPath: string, index?: number, cb?: ErrCallback): void {
    const path = this.arrayInfo(configPath);

    // Implementing callbacks correctly, if one is provided.
    if (typeof cb === 'function') {
      // Pass a wrapper callback to arrayRemove
      this.storageService.arrayRemove(path, index, () => {
        cb(); // Ensure the original callback is called after arrayRemove completes
      });
    } else {
      // If no callback is provided, just call arrayRemove
      this.storageService.arrayRemove(path, index);
    }
  }

  /**
   * Tell the length of the array based on the '$' in the stateful value ID (aka path)
   * @param configPath stateful value ID (should include the '$' or '#')
   * @param pageIterationId required when page is iterated (pathes with #)
   * @param throwOnMissingArray throws error if array is missing (defaults to returning 0)
   */
  public arraySize(configPath: string, pageIterationId?: number, throwOnMissingArray?: boolean): number {
    return statefulArrayHelper.arraySize(configPath, pageIterationId, throwOnMissingArray);
  }

  /**
   * Explicit request to empty an array
   * This should be the only way for the UI to trigger array shrinking (to avoid race conditions)
   * NOTE: addition on the backend may be asynchronous. Don't expect the array to be changed right after calling this method.
   * Use the callback, if needed
   * @param storageId including iteration ID (not $)
   */
  public arrayTruncate(storageId: string, cb?: ErrCallback): void {
    const path = this.arrayInfo(storageId);

    // Implementing callbacks correctly, if one is provided.
    if (typeof cb === 'function') {
      // Pass a wrapper callback to arrayTruncate
      this.storageService.arrayTruncate(path, () => {
        cb(); // Ensure the original callback is called after arrayTruncate completes
      });
    } else {
      // If no callback is provided, just call arrayTruncate
      this.storageService.arrayTruncate(path);
    }
  }

  /**
   * Explicit request to empty an array
   * This should be the only way for the UI to trigger array shrinking (to avoid race conditions)
   * NOTE: addition on the backend may be asynchronous. Don't expect the array to be changed right after calling this method.
   * Use the callback, if needed.
   * NOTE 2: This does not get array info - use with caution, and assert your path beforehand.
   * @param storageId including iteration ID (not $)
   */
  public arrayTruncateWithPageIteration(storageId: string, cb?: ErrCallback): void {
    // Implementing callbacks correctly, if one is provided.
    if (typeof cb === 'function') {
      // Pass a wrapper callback to arrayTruncate
      this.storageService.arrayTruncate(storageId, () => {
        cb(); // Ensure the original callback is called after arrayTruncate completes
      });
    } else {
      // If no callback is provided, just call arrayTruncate
      this.storageService.arrayTruncate(storageId);
    }
  }

  /**
   * Check if array root is being adjusted on backend
   * (equals to backend.isLoading for now - it covers both init, and any load/save that can change arrays)
   * @param configPath including iteration ID (not $)
   */
  public isArrayAdjusting(configPath: string): boolean {
    const backend = this.storageService.backends[configPath.split(':')[0]];
    return backend?.isLoading;
  }

  /**
   * Values are updated (or added) on bundle data receival
   * @param data propagated dictionary of changes
   */
  public onBundleUpdate(data: FlatData): void {
    for (const [storageId, value] of Object.entries(data)) {
      const valueConfig = this.storageInfoService.parse(storageId);
      if (valueConfig) {
        this.wrap(valueConfig.storageId, valueConfig.iterationId, valueConfig.pageIterationId).setSavedValue(value);
      }
    }
  }

  /**
   * Below method should be called while switching the bundle to refresh all storage ids
   * Make sure this method called only once
   */
  public reloadData(): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [, instance] of Object.entries(this.instances)) {
      instance.revert();
    }
  }

  /**
   * Below method should be called to clear statefulValue
   * Make sure this method called only once
   */
  public clear(): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [id] of Object.entries(this.instances)) {
      delete this.instances[id];
    }
    this.stateManagementService.clear();
  }

  /**
   * Safely convert values, such as "-$123,323.32" to number types (-123323.32)
   * @param value any primitive type
   */
  public numericValue(value: PrimitiveType): number {
    if (typeof value === 'number' || value == null) {
      return value as number;
    }
    if (typeof value === 'boolean') {
      return value ? 1 : 0;
    }
    // tslint:disable-next-line: no-magic-numbers
    const str = String(value).replace(SIGNED_FLOAT_RX, '').replace(START_SIGN_RX, '').split('.').slice(0, 2).join('.');
    return str ? +str : null;
  }

  /**
   * Test configuration values for validity + set sane defaults (e.g. empty object where missing, adding implicit IDs, etc)
   * Throws errors on integration fails
   * @param id card ID from `workflow-cards.json`
   */
  public test(backendType: Backend, id: string): void {
    const config = this.configService.config.storage[backendType][id];
    this.ensureConfig(config, id);
  }

  public ensureConfig(config: Storage, id: string): void {
    // ensure consistency with IDs
    config.id = id;

    // ensure existing type
    config.type = config.type || 'string';

    // ensure existing validation
    config.validation = config.validation || {};
  }

  /**
   * URL info on path. Will redirect for arrayLink
   * @param configPath path to examine
   */
  private arrayInfo(configPath: string): string {
    return statefulArrayHelper.arrayInfo(configPath);
  }

  /**
   * Below method should be called to clear statefulValue
   * Make sure this method called only once
   */
  public saveChangedValues(): Promise<boolean[]> {
    const promises: Promise<boolean>[] = [];
    for (const [id, instance] of Object.entries(this.instances)) {
      if (instance.isChanged) {
        const promise = this.instances[id].save();
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        if (promise) promises.push(promise);
      }
    }
    return Promise.all(promises);
  }
}
